// Site and lift history on the engineer PWA (Session AEK, Director decision D33).
//
// Why this exists: an engineer attending a lift they have never worked on needs to know what the
// previous engineer found and did, and whether anything is still outstanding on it. Until AEK the
// PWA could only show the engineer their OWN assigned jobs, so that continuity was lost every
// time a different engineer attended.
//
// WHOSE SITES. No longer company-wide: since Session AGS only the sites the engineer had work at,
// and since Session ASB (Director decision D-SEC-6, 25 September 2026) only the sites where they hold
// OPEN work now (a job not yet complete and approved, or an upcoming training session). When that
// work is finished the site leaves this list, its search and its history screens, on the server
// (a 404) and on this device (the next sync prunes its saved pages; a 404 drops the page it was
// asked for). ELE's PWA logins include subcontractors, which is why.
//
// Read-only throughout. Three screens: the site browse list, one site's history, one lift's
// history. Each caches through IndexedDB (see historyCache.ts), so a site visited once is
// readable again in a basement with no signal, clearly labelled when what is shown is saved
// rather than live.
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  loadSiteList,
  loadSiteHistory,
  loadLiftHistory,
  filterSites,
  splitSitesByArchived,
} from "../../lib/offline/historyStore";
import type { EngineerSiteListItem, EngineerHistoryJob, EngineerHistoryDefect } from "../../lib/api";
import JobTypeBadge from "../../components/JobTypeBadge";

// Append a page, dropping any visit already on screen. Offset pagination reads each page at a
// different moment, so a visit completed between two fetches shifts the boundary and can repeat a
// row; without this the engineer sees the same visit twice under a duplicate React key.
function mergeVisits(prev: EngineerHistoryJob[], next: EngineerHistoryJob[]): EngineerHistoryJob[] {
  const seen = new Set(prev.map((v) => v.id));
  return [...prev, ...next.filter((v) => !seen.has(v.id))];
}

function dateLabel(iso: string | null): string {
  if (!iso) return "Date not recorded";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "Date not recorded"
    : d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" });
}

// Session ASB (D-SEC-6): the server answers 404 for a site or lift the engineer no longer has open work
// at. Said as what it is, in plain English, rather than as "could not load, try when back online",
// which would send them hunting for signal that will not help.
function isGone(err: unknown): boolean {
  return (err as { status?: unknown } | null)?.status === 404;
}

function NoLongerListed({ what }: { what: "site" | "lift" }) {
  return (
    <div className="rounded-ele border border-ele-purple-accent bg-ele-surface p-6 text-center text-ele-body">
      {what === "site"
        ? "This site is no longer on your list. A site appears here while you have work booked there."
        : "This lift is no longer on your list. A lift appears here while you have work booked at its site."}
    </div>
  );
}

// Shown whenever a screen is serving cached data that is past its lifetime because the network
// could not be reached. Old history beats a blank screen underground, but never silently.
function StaleNotice() {
  return (
    <p className="mb-3 rounded-ele border border-ele-purple-accent bg-ele-surface px-3 py-2 text-xs text-ele-body">
      Showing saved information. It will update when you are back online.
    </p>
  );
}

function BackLink({ to, label }: { to: string; label: string }) {
  return (
    <Link to={to} className="mb-3 inline-flex min-h-11 items-center font-menu text-xs tracking-wider text-ele-body">
      Back to {label}
    </Link>
  );
}

// A completed visit. The engineer, the date, the type and a short account of what was done: the
// four things that answer "what happened here last time".
//
// Since Session AEM the whole row taps through to the record itself: the summary below is a
// taster, and an engineer picking up an unfamiliar lift needs the full checklist, notes and
// defects rather than a paraphrase. `from` is carried so Back returns to the screen the visit was
// opened from, not always the browse list.
function VisitRow({ job, from }: { job: EngineerHistoryJob; from: string }) {
  return (
    <li>
      <Link
        to={`/engineer/history/visits/${job.id}`}
        state={{ from }}
        className="block rounded-ele border border-ele-purple-accent bg-ele-surface p-3"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="font-heading text-sm text-ele-strong">{dateLabel(job.completedAt)}</p>
            {job.eleNumber && <p className="mt-0.5 text-xs text-ele-body">{job.eleNumber}</p>}
          </div>
          <JobTypeBadge type={job.type} size="sm" />
        </div>
        <p className="mt-2 text-sm text-ele-body">{job.outcome}</p>
        {job.engineerName && <p className="mt-1 text-xs text-ele-body opacity-70">Attended by {job.engineerName}</p>}
      </Link>
    </li>
  );
}

// A page that failed AFTER the first one loaded. Without this the Load more button simply does
// nothing and the engineer is left tapping it.
function PagingError() {
  return (
    <p role="alert" className="mt-3 text-sm text-amber-400">
      Could not load any more just now. Try again when you have signal.
    </p>
  );
}

function LoadMore({ onClick, busy }: { onClick: () => void; busy: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      className="mt-3 flex min-h-11 w-full items-center justify-center rounded-ele border border-ele-field-border font-menu text-xs tracking-wider text-ele-body disabled:opacity-50"
    >
      {busy ? "Loading..." : "Load more"}
    </button>
  );
}

// ---- Screen 1: the site browse list -----------------------------------------------------

// One row in either section. An archived site is marked by SAYING SO, not by dimming it: a
// blanket opacity on the row drops the small blue ELE-number line below the readable contrast
// floor, and that line is the one field an engineer uses to tell whether this is the right
// building. It is muted only where muting costs nothing (the site name takes the body colour
// instead of white) and carries an explicit tag. Same shape, same tap target, same destination.
function SiteRow({ site, muted }: { site: EngineerSiteListItem; muted: boolean }) {
  return (
    <li>
      <Link
        to={`/engineer/history/sites/${site.id}`}
        className="block rounded-ele border border-ele-purple-accent bg-ele-surface p-3"
      >
        <p className={`font-heading text-base ${muted ? "text-ele-body" : "text-ele-strong"}`}>{site.name}</p>
        <p className="mt-0.5 text-sm text-ele-body">{site.address}</p>
        {site.eleNumbers.length > 0 && (
          <p className="mt-1 font-menu text-xs uppercase tracking-wider text-ele-blue">{site.eleNumbers.join(", ")}</p>
        )}
        {muted && <p className="mt-1 font-menu text-xs uppercase tracking-wider text-ele-body">No longer serviced</p>}
      </Link>
    </li>
  );
}

// Since Session AGS this list holds only the engineer's own sites, not every site on the account,
// and since Session ASB (D-SEC-6) only the sites where they have work booked NOW, so an empty list is
// the ordinary state for someone between jobs. The old wording ("No sites on record yet") became
// simply untrue at AGS -- there are sites on record, they are just not this engineer's -- and it would
// send someone hunting a data fault that does not exist. It says what the list is instead.
const EMPTY_SITE_LIST = "No sites yet. A site appears here while you have work booked there.";

export function EngineerSiteList() {
  const [sites, setSites] = useState<EngineerSiteListItem[] | null>(null);
  const [stale, setStale] = useState(false);
  const [error, setError] = useState(false);
  const [query, setQuery] = useState("");
  // null means "follow the search": the archived section opens by itself when a search matches
  // something in it, and closes again when it does not. A tap sets it explicitly, and typing
  // again hands control back to the search, so an engineer hunting a decommissioned site is
  // never blocked by a collapsed section they cannot see the contents of.
  const [archivedOpen, setArchivedOpen] = useState<boolean | null>(null);

  useEffect(() => {
    let active = true;
    loadSiteList()
      .then((res) => {
        if (!active) return;
        setSites(res.data);
        setStale(res.stale);
      })
      .catch(() => {
        if (active) setError(true);
      });
    return () => {
      active = false;
    };
  }, []);

  // Search FIRST, split second: the search has to be able to reach an archived site.
  const shown = useMemo(() => filterSites(sites ?? [], query), [sites, query]);
  const { active, archived } = useMemo(() => splitSitesByArchived(shown), [shown]);

  const searching = query.trim() !== "";
  const showArchived = archivedOpen ?? (searching && archived.length > 0);

  return (
    <div>
      <h1 className="mb-3 font-heading text-2xl tracking-wide text-ele-strong">Site and lift history</h1>
      {stale && <StaleNotice />}
      <input
        type="search"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setArchivedOpen(null); // a new search decides the section again
        }}
        placeholder="Search by site, postcode or ELE number"
        aria-label="Search sites"
        className="mb-4 min-h-11 w-full rounded-ele border border-ele-field-border bg-ele-surface px-3 text-sm text-ele-strong placeholder:text-ele-body"
      />

      {error && !sites ? (
        <div className="rounded-ele border border-ele-purple-accent bg-ele-surface p-6 text-center text-ele-body">
          Could not load the site list just now. It will appear when you are back online.
        </div>
      ) : !sites ? (
        <p className="text-sm text-ele-body">Loading...</p>
      ) : shown.length === 0 ? (
        <p className="text-sm text-ele-body">{query ? "No sites match that search." : EMPTY_SITE_LIST}</p>
      ) : (
        <>
          <h2 className="mb-2 font-menu text-xs uppercase tracking-wider text-ele-body">Sites</h2>
          {active.length === 0 ? (
            <p className="text-sm text-ele-body">
              {searching ? "No current sites match that search." : EMPTY_SITE_LIST}
            </p>
          ) : (
            <ul className="space-y-2">
              {active.map((site) => (
                <SiteRow key={site.id} site={site} muted={false} />
              ))}
            </ul>
          )}

          {archived.length > 0 && (
            <section className="mt-6">
              <button
                type="button"
                onClick={() => setArchivedOpen(!showArchived)}
                aria-expanded={showArchived}
                className="flex min-h-11 w-full items-center justify-between rounded-ele border border-ele-field-border bg-ele-surface px-3 font-menu text-xs tracking-wider text-ele-body"
              >
                <span>Archived sites ({archived.length})</span>
                <span aria-hidden="true">{showArchived ? "⌃" : "⌄"}</span>
              </button>
              {showArchived && (
                <>
                  <p className="mt-2 text-xs text-ele-body opacity-70">
                    ELE no longer services these sites. Their past service history is kept here.
                  </p>
                  <ul className="mt-2 space-y-2">
                    {archived.map((site) => (
                      <SiteRow key={site.id} site={site} muted />
                    ))}
                  </ul>
                </>
              )}
            </section>
          )}
        </>
      )}
    </div>
  );
}

// ---- Screen 2: one site's history --------------------------------------------------------

// The site's access details (Session AFR): door codes, fob/key location, parking, motor-room
// access method, building hours -- what the engineer needs BEFORE work begins, so it sits at the
// TOP of the site view, above the lifts and the visit history. The locked functional-amber panel
// (operationally important, not a fault or defect); it carries its own cream background so it reads
// correctly in either theme. Absent entirely when nothing is recorded (never an empty box, never a
// "None"). Display-only here: these history screens are read-only; the office edits the field.
function AccessDetailsPanel({ value }: { value: string }) {
  return (
    <section className="mt-4 rounded-ele border border-[#b8860b] bg-[#fbf3dd] p-4">
      <h2 className="font-menu text-xs uppercase tracking-wider text-ele-amber-wash-ink">Site access</h2>
      <p className="mt-1 whitespace-pre-wrap text-sm text-ele-amber-wash-ink">{value}</p>
    </section>
  );
}

export function EngineerSiteHistoryPage() {
  const { siteId = "" } = useParams();
  const [header, setHeader] = useState<{ name: string; address: string; accessDetails: string | null } | null>(null);
  const [lifts, setLifts] = useState<{ id: string; eleNumber: string | null; liftType: string | null }[]>([]);
  const [visits, setVisits] = useState<EngineerHistoryJob[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [stale, setStale] = useState(false);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState(false);
  // Session ASB: the server said this site is not the engineer's to read any more (404).
  const [gone, setGone] = useState(false);

  const load = useCallback(
    async (next: number) => {
      setBusy(true);
      setError(false);
      if (next === 1) setGone(false); // a fresh screen (a different site or lift) asks again
      try {
        const res = await loadSiteHistory(siteId, next);
        const { site, lifts: rows, items, hasMore: more } = res.data;
        setHeader({ name: site.name, address: site.address, accessDetails: site.accessDetails ?? null });
        setLifts(rows);
        // Append on a later page, replace on the first: paging forward never loses what is above.
        // De-duplicated by id, because each page is fetched at a different time and a visit
        // completed in between shifts the offset boundary, which can repeat a row.
        setVisits((prev) => (next === 1 ? items : mergeVisits(prev, items)));
        setHasMore(more);
        setPage(next);
        // Once ANY page on screen came from a stale cache the notice stays: clearing it because a
        // later page happened to load live would pass day-old rows off as current.
        setStale((prev) => (next === 1 ? res.stale : prev || res.stale));
      } catch (err) {
        if (isGone(err)) setGone(true);
        setError(true);
      } finally {
        setBusy(false);
      }
    },
    [siteId],
  );

  useEffect(() => {
    void load(1);
  }, [load]);

  // Whatever is already on screen goes too: the server has said this site is no longer readable.
  if (gone) {
    return (
      <div>
        <BackLink to="/engineer/history" label="site list" />
        <NoLongerListed what="site" />
      </div>
    );
  }

  if (error && !header) {
    return (
      <div>
        <BackLink to="/engineer/history" label="site list" />
        <div className="rounded-ele border border-ele-purple-accent bg-ele-surface p-6 text-center text-ele-body">
          Could not load this site just now. It will appear when you are back online.
        </div>
      </div>
    );
  }

  return (
    <div>
      <BackLink to="/engineer/history" label="site list" />
      {stale && <StaleNotice />}
      <h1 className="font-heading text-2xl tracking-wide text-ele-strong">{header?.name ?? "Site"}</h1>
      {header && <p className="mt-1 text-sm text-ele-body">{header.address}</p>}

      {/* Access details FIRST (Session AFR): the engineer needs to get in before anything else. */}
      {header?.accessDetails && <AccessDetailsPanel value={header.accessDetails} />}

      <h2 className="mt-5 font-menu text-xs uppercase tracking-wider text-ele-body">Lifts at this site</h2>
      {lifts.length === 0 ? (
        <p className="mt-2 text-sm text-ele-body">No lifts on record at this site.</p>
      ) : (
        <ul className="mt-2 space-y-2">
          {lifts.map((lift) => (
            <li key={lift.id}>
              <Link
                to={`/engineer/history/lifts/${lift.id}`}
                className="flex min-h-11 items-center justify-between rounded-ele border border-ele-field-border bg-ele-surface px-3"
              >
                <span className="font-heading text-sm text-ele-strong">{lift.eleNumber ?? "No ELE number"}</span>
                <span className="text-xs text-ele-body">{lift.liftType ?? ""}</span>
              </Link>
            </li>
          ))}
        </ul>
      )}

      <h2 className="mt-6 font-menu text-xs uppercase tracking-wider text-ele-body">Completed visits</h2>
      {visits.length === 0 && !busy ? (
        <p className="mt-2 text-sm text-ele-body">No completed visits recorded at this site yet.</p>
      ) : (
        <ul className="mt-2 space-y-2">
          {visits.map((job) => (
            <VisitRow key={job.id} job={job} from={`/engineer/history/sites/${siteId}`} />
          ))}
        </ul>
      )}
      {error && <PagingError />}
      {hasMore && <LoadMore busy={busy} onClick={() => void load(page + 1)} />}
    </div>
  );
}

// ---- Screen 3: one lift's history --------------------------------------------------------

// Outstanding defects are the reason this screen matters most: an engineer must know what is
// already wrong with a lift before they start work on it. Amber and above the history, never
// buried below it.
// An "immediate" defect is the Category A case: something that makes the lift unsafe to leave in
// service. It must not look like a worn guide shoe. The house palette reserves the danger token
// for immediate danger, so the alert takes its tone from the worst defect present, and the raw
// enum key is never rendered.
function severityLabel(severity: string): string {
  if (severity === "immediate") return "Immediate action";
  if (severity === "advisory") return "Advisory";
  return severity.replace(/_/g, " ");
}

function OpenDefects({ defects }: { defects: EngineerHistoryDefect[] }) {
  if (defects.length === 0) return null;
  const anyImmediate = defects.some((d) => d.severity === "immediate");
  const box = anyImmediate ? "border-ele-danger bg-[rgba(255,90,90,0.1)]" : "border-amber-500 bg-amber-500/10";
  const heading = anyImmediate ? "text-ele-danger" : "text-[var(--ele-amber-text)]";
  return (
    <div role="alert" className={`mt-4 rounded-ele border p-4 ${box}`}>
      <p className={`font-heading text-sm uppercase tracking-wide ${heading}`}>
        {defects.length === 1 ? "Outstanding defect" : `${defects.length} outstanding defects`}
      </p>
      <ul className="mt-2 space-y-2">
        {defects.map((defect) => (
          <li key={defect.id}>
            <p
              className={`font-menu text-xs uppercase tracking-wider ${
                defect.severity === "immediate" ? "text-ele-danger" : "text-[var(--ele-amber-text)]"
              }`}
            >
              {severityLabel(defect.severity)}
            </p>
            <p className="text-sm text-ele-strong">{defect.description}</p>
            {defect.raisedAt && <p className="text-xs text-ele-body opacity-70">Raised {dateLabel(defect.raisedAt)}</p>}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function EngineerLiftHistoryPage() {
  const { assetId = "" } = useParams();
  const [header, setHeader] = useState<{ eleNumber: string | null; liftType: string | null; siteName: string | null } | null>(
    null,
  );
  const [defects, setDefects] = useState<EngineerHistoryDefect[]>([]);
  const [visits, setVisits] = useState<EngineerHistoryJob[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [stale, setStale] = useState(false);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState(false);
  // Session ASB: the server said this lift is not the engineer's to read any more (404).
  const [gone, setGone] = useState(false);

  const load = useCallback(
    async (next: number) => {
      setBusy(true);
      setError(false);
      if (next === 1) setGone(false); // a fresh screen (a different site or lift) asks again
      try {
        const res = await loadLiftHistory(assetId, next);
        const { asset, defects: open, items, hasMore: more } = res.data;
        setHeader({ eleNumber: asset.eleNumber, liftType: asset.liftType, siteName: asset.siteName });
        setDefects(open);
        setVisits((prev) => (next === 1 ? items : mergeVisits(prev, items)));
        setHasMore(more);
        setPage(next);
        setStale((prev) => (next === 1 ? res.stale : prev || res.stale));
      } catch (err) {
        if (isGone(err)) setGone(true);
        setError(true);
      } finally {
        setBusy(false);
      }
    },
    [assetId],
  );

  useEffect(() => {
    void load(1);
  }, [load]);

  if (gone) {
    return (
      <div>
        <BackLink to="/engineer/history" label="site list" />
        <NoLongerListed what="lift" />
      </div>
    );
  }

  if (error && !header) {
    return (
      <div>
        <BackLink to="/engineer/history" label="site list" />
        <div className="rounded-ele border border-ele-purple-accent bg-ele-surface p-6 text-center text-ele-body">
          Could not load this lift just now. It will appear when you are back online.
        </div>
      </div>
    );
  }

  return (
    <div>
      <BackLink to="/engineer/history" label="site list" />
      {stale && <StaleNotice />}
      <h1 className="font-heading text-2xl tracking-wide text-ele-strong">{header?.eleNumber ?? "Lift"}</h1>
      <p className="mt-1 text-sm text-ele-body">
        {[header?.liftType, header?.siteName].filter(Boolean).join(" | ")}
      </p>

      <OpenDefects defects={defects} />

      {/* Fault Assistant (Session AFC): ask about a fault on this lift, drawing on the knowledge
          base and this lift's own history. Below the header and any outstanding-defect alert, above
          the visit history. */}
      <Link
        to={`/engineer/fault-assistant?assetId=${assetId}`}
        className="mt-4 flex min-h-11 w-full items-center justify-center rounded-ele border border-ele-blue py-3 text-center font-heading text-sm tracking-wide text-ele-blue transition hover:bg-ele-blue/10"
      >
        Fault Assistant
      </Link>

      <h2 className="mt-6 font-menu text-xs uppercase tracking-wider text-ele-body">Completed visits</h2>
      {visits.length === 0 && !busy ? (
        <p className="mt-2 text-sm text-ele-body">No completed visits recorded on this lift yet.</p>
      ) : (
        <ul className="mt-2 space-y-2">
          {visits.map((job) => (
            <VisitRow key={job.id} job={job} from={`/engineer/history/lifts/${assetId}`} />
          ))}
        </ul>
      )}
      {error && <PagingError />}
      {hasMore && <LoadMore busy={busy} onClick={() => void load(page + 1)} />}
    </div>
  );
}
